import { API_BASE } from '../api/http';
import { isChunkLoadError } from '../routes/lazy-page';

/**
 * Browser error tracking (docs/MONITORING.md): uncaught errors, rejected promises and errors that
 * reach a page's error boundary are posted to the API (POST /api/v2/client-errors), which
 * fingerprints, counts and logs them. Kept small and quiet:
 * - once per distinct error, at most 10 per page load;
 * - nothing while the document is being left (WebKit rejects the cancelled requests then);
 * - chunk-load failures are not errors (lazyPage reloads into the new build);
 * - the page path only, never the query string.
 */
const MAX_PER_LOAD = 10;
const RELEASE = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';
const seen = new Set<string>();
let leaving = false;

export type ClientErrorKind = 'error' | 'rejection' | 'boundary';

export function reportClientError(kind: ClientErrorKind, err: unknown): void {
  if (leaving || seen.size >= MAX_PER_LOAD || isChunkLoadError(err)) return;
  const message = (err instanceof Error ? err.message : String(err)).slice(0, 1000) || 'unknown';
  const stack = err instanceof Error ? (err.stack ?? '').slice(0, 8000) : '';
  const key = `${kind}|${message}|${stack.split('\n')[1] ?? ''}`;
  if (seen.has(key)) return;
  seen.add(key);
  void fetch(`${API_BASE}/api/v2/client-errors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({
      kind,
      message,
      ...(stack ? { stack } : {}),
      page: window.location.pathname.slice(0, 300),
      release: RELEASE.slice(0, 40),
    }),
    keepalive: true,
  }).catch(() => undefined); // reporting must never raise an error of its own
}

/** Once, at start-up (main.tsx). */
export function installErrorReporting(): void {
  for (const type of ['beforeunload', 'pagehide'])
    window.addEventListener(type, () => (leaving = true));
  window.addEventListener('error', (e) => reportClientError('error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => reportClientError('rejection', e.reason));
}

/** for tests */
export function resetErrorReporting(): void {
  seen.clear();
  leaving = false;
}
